import { ReactNode, useMemo } from "react";
import { Control, FieldErrors, UseFormReturn } from "react-hook-form";
import { Form } from "../../../../components/ui/common/form";

interface RegisterFormProviderProps {
  control: Control<Record<string, unknown>>;
  errors: FieldErrors<Record<string, unknown>>;
  children: ReactNode;
}

/**
 * Bridges the register form's `control`/`errors` (from the legacy
 * `useSupplierRegisterForm`) into a `<Form>` (react-hook-form's
 * `FormProvider`) context, so the common `FormInput`/`FormInputNumber`/
 * `FormSelect` components (src/components/ui/Input/) can be used here —
 * those all read error state via `useFormField()` -> `useFormContext()`,
 * which throws without a real `<FormProvider>` ancestor.
 *
 * `useSupplierRegisterForm` only returns destructured pieces of its
 * `useForm()` call (`register`, `control`, `errors`, ...), not the full
 * `UseFormReturn` object `<FormProvider>` normally expects — and it can't be
 * changed (one of the 6 protected legacy hooks). Every `FormLabel`/
 * `FormControl`/`FormMessage` in this codebase's `useFormField()` only ever
 * reads `getFieldState(name, formState)` from that context (see
 * src/components/ui/common/form.tsx) — nothing else — so this synthesizes
 * just that, from the same `errors` object the fields are already bound to
 * via `control`. The fields themselves (`Controller`, driven by `control`)
 * don't need this at all; only the label/error chrome does.
 */
const RegisterFormProvider = ({ control, errors, children }: RegisterFormProviderProps) => {
  const formMethods = useMemo(() => {
    const getFieldState = (name: string) => {
      const error = (errors as Record<string, unknown>)[name];
      return { invalid: Boolean(error), isDirty: false, isTouched: false, error };
    };

    return {
      control,
      formState: { errors },
      getFieldState
    } as unknown as UseFormReturn<Record<string, unknown>>;
  }, [control, errors]);

  return <Form {...formMethods}>{children}</Form>;
};

export default RegisterFormProvider;
